import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { authApi } from "../../api/auth";
import { Button } from "../../components/ui/Button";
import { ButtonLink } from "../../components/ui/ButtonLink";
import { Input } from "../../components/ui/Input";
import { Card } from "../../components/ui/Card";

const resetSchema = z
  .object({
    code: z.string().length(6, "Code must be 6 digits"),
    newPassword: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type ResetForm = z.infer<typeof resetSchema>;

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const emailFromParams = searchParams.get("email") || "";
  const codeFromParams = searchParams.get("code") || "";

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const autoFilled = useRef(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ResetForm>({
    resolver: zodResolver(resetSchema),
    defaultValues: { code: codeFromParams },
  });

  useEffect(() => {
    if (codeFromParams && !autoFilled.current) {
      autoFilled.current = true;
      setValue("code", codeFromParams);
    }
  }, [codeFromParams, setValue]);

  const onSubmit = async (data: ResetForm) => {
    if (!emailFromParams) {
      setError("Email address is missing. Please use the link from your email.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await authApi.resetPassword(emailFromParams, data.code, data.newPassword);
      if (!result.success) {
        setError("Invalid or expired reset code. Please request a new one.");
        return;
      }
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-alt p-4">
        <Card className="w-full max-w-md" title={t("auth.passwordResetSuccessTitle")}>
          <p className="text-text-muted mb-4">
            {t("auth.passwordResetSuccessDesc")}
          </p>
          <ButtonLink to="/login" className="w-full">
            {t("auth.signIn")}
          </ButtonLink>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-alt p-4">
      <Card className="w-full max-w-md" title={t("auth.resetYourPassword")}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}

          {emailFromParams && (
            <p className="text-sm text-text-muted">
              {t("auth.resettingPasswordFor")} <strong>{emailFromParams}</strong>
            </p>
          )}

          <Input
            label={t("auth.resetCodeLabel")}
            type="text"
            inputMode="numeric"
            placeholder="123456"
            maxLength={6}
            error={errors.code?.message}
            {...register("code")}
          />

          <Input
            label={t("auth.newPasswordLabel")}
            type="password"
            placeholder="••••••••"
            error={errors.newPassword?.message}
            {...register("newPassword")}
          />

          <Input
            label={t("auth.confirmNewPasswordLabel")}
            type="password"
            placeholder="••••••••"
            error={errors.confirmPassword?.message}
            {...register("confirmPassword")}
          />

          <Button type="submit" className="w-full" loading={loading}>
            {t("auth.resetPasswordButton")}
          </Button>

          <p className="text-center text-sm text-text-muted">
            <Link to="/forgot-password" className="text-brand hover:underline">
              {t("auth.requestNewCodeLink")}
            </Link>
            {" · "}
            <Link to="/login" className="text-brand hover:underline">
              {t("auth.backToLogin")}
            </Link>
          </p>
        </form>
      </Card>
    </div>
  );
}
